const VIDEO_CACHE_NAME = "video-precache";
const CONCURRENCY = 3;

export interface CacheProgress {
  videoUrl: string;
  segmentsCached: number;
  totalSegments: number;
  phase: "manifest" | "segments" | "done" | "error";
  error?: string;
}

export interface CacheStatus {
  cached: boolean;
  segmentsCached: number;
  totalSegments: number;
}

type ProgressCallback = (progress: CacheProgress) => void;
let progressListeners: ProgressCallback[] = [];

const notifyProgress = (progress: CacheProgress) => {
  progressListeners.forEach(cb => cb(progress));
};

// --- HLS Manifest Parser ---

const parseMasterManifest = (text: string, baseUrl: string) => {
  const lines = text.split("\n");
  const variants: { bandwidth: number; resolution: string; url: string }[] = [];
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();
    if (line.startsWith("#EXT-X-STREAM-INF:")) {
      const bw = line.match(/BANDWIDTH=(\d+)/);
      const res = line.match(/RESOLUTION=([\dx]+)/);
      const next = (lines[i + 1] || "").trim();
      if (next && !next.startsWith("#")) {
        variants.push({
          bandwidth: bw ? parseInt(bw[1], 10) : 0,
          resolution: res ? res[1] : "unknown",
          url: new URL(next, baseUrl).href,
        });
      }
    }
  }
  return variants;
};

const parseSegmentUrls = (text: string, baseUrl: string) =>
  text
    .split("\n")
    .map(l => l.trim())
    .filter(l => l && !l.startsWith("#"))
    .map(path => new URL(path, baseUrl).href);

const baseUrlOf = (url: string) => url.substring(0, url.lastIndexOf("/") + 1);

export const toAbsoluteUrl = (url: string) =>
  url.startsWith("http") ? url : new URL(url, window.location.origin).href;

const selectTargetVariant = (
  variants: { bandwidth: number; resolution: string; url: string }[],
) => {
  const sorted = [...variants].sort((a, b) => a.bandwidth - b.bandwidth);
  return (
    sorted.find(v => v.resolution.includes("720")) || sorted[sorted.length - 1]
  );
};

// ABR（低帯域時の自動画質切替）が非キャッシュvariantへ切り替わるのを防ぐ。
const filterMasterManifestToTarget = (
  text: string,
  targetUrl: string,
  baseUrl: string,
): string => {
  const lines = text.split("\n");
  const out: string[] = [];
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const trimmed = line.trim();

    // STREAM-INF ヘッダ + 次行のURLをペアで処理
    if (trimmed.startsWith("#EXT-X-STREAM-INF:")) {
      const nextLine = (lines[i + 1] || "").trim();
      if (nextLine && !nextLine.startsWith("#")) {
        const absUrl = new URL(nextLine, baseUrl).href;
        if (absUrl === targetUrl) {
          out.push(line);
          out.push(lines[i + 1]);
        }
        i += 2;
        continue;
      }
      i++;
      continue;
    }

    // I-frameトリック再生用variantは対応していないので除去
    if (trimmed.startsWith("#EXT-X-I-FRAME-STREAM-INF:")) {
      i++;
      continue;
    }

    out.push(line);
    i++;
  }
  return out.join("\n");
};

const fetchVideo = async (url: string) => {
  try {
    return await fetch(url, { credentials: "include" });
  } catch {
    return fetch(url);
  }
};

// --- Cache Video ---

export async function cacheVideo(masterManifestUrl: string): Promise<void> {
  masterManifestUrl = toAbsoluteUrl(masterManifestUrl);

  const estimate = await navigator.storage?.estimate();
  if (estimate) {
    const usageRatio = (estimate.usage ?? 0) / (estimate.quota ?? 1);
    if (usageRatio > 0.9) {
      throw new Error(
        "ストレージの空き容量が不足しています。キャッシュをクリアしてください。",
      );
    }
  }

  notifyProgress({
    videoUrl: masterManifestUrl,
    segmentsCached: 0,
    totalSegments: 0,
    phase: "manifest",
  });

  const cache = await caches.open(VIDEO_CACHE_NAME);

  const masterResponse = await fetchVideo(masterManifestUrl);
  const masterText = await masterResponse.text();

  const baseUrl = baseUrlOf(masterManifestUrl);
  const variants = parseMasterManifest(masterText, baseUrl);

  if (variants.length === 0) {
    await cache.put(
      masterManifestUrl,
      new Response(masterText, { headers: { date: new Date().toUTCString() } }),
    );
    await cacheSegments(cache, masterText, baseUrl, masterManifestUrl);
    return;
  }

  const target = selectTargetVariant(variants);

  // 低速時のABR切替で非キャッシュvariantへ逃げる現象を防ぐ。
  const filteredMasterText = filterMasterManifestToTarget(
    masterText,
    target.url,
    baseUrl,
  );
  await cache.put(
    masterManifestUrl,
    new Response(filteredMasterText, {
      headers: { date: new Date().toUTCString() },
    }),
  );

  const targetRes = await fetchVideo(target.url);
  const targetText = await targetRes.text();
  await cache.put(
    target.url,
    new Response(targetText, { headers: { date: new Date().toUTCString() } }),
  );

  await cacheSegments(
    cache,
    targetText,
    baseUrlOf(target.url),
    masterManifestUrl,
  );
}

async function cacheSegments(
  cache: Cache,
  variantText: string,
  baseUrl: string,
  videoUrl: string,
): Promise<void> {
  const segmentUrls = parseSegmentUrls(variantText, baseUrl);
  const total = segmentUrls.length;

  for (let i = 0; i < total; i += CONCURRENCY) {
    const batch = segmentUrls.slice(i, i + CONCURRENCY);
    await Promise.all(
      batch.map(async (url, j) => {
        if (!(await cache.match(url))) {
          const res = await fetchVideo(url);
          await cache.put(url, res);
        }
        notifyProgress({
          videoUrl,
          segmentsCached: i + j + 1,
          totalSegments: total,
          phase: "segments",
        });
      }),
    );
  }

  notifyProgress({
    videoUrl,
    segmentsCached: total,
    totalSegments: total,
    phase: "done",
  });
}

// --- Get Cache Status ---

export async function getCacheStatus(
  masterManifestUrl: string,
): Promise<CacheStatus> {
  masterManifestUrl = toAbsoluteUrl(masterManifestUrl);
  const empty = { cached: false, segmentsCached: 0, totalSegments: 0 };
  try {
    const cache = await caches.open(VIDEO_CACHE_NAME);
    const masterResponse = await cache.match(masterManifestUrl);
    if (!masterResponse) return empty;

    const masterText = await masterResponse.text();
    const baseUrl = baseUrlOf(masterManifestUrl);
    const variants = parseMasterManifest(masterText, baseUrl);

    let variantText: string;
    let variantBaseUrl: string;

    if (variants.length === 0) {
      variantText = masterText;
      variantBaseUrl = baseUrl;
    } else {
      const target = selectTargetVariant(variants);
      variantBaseUrl = baseUrlOf(target.url);
      const res = await cache.match(target.url);
      if (!res) return empty;
      variantText = await res.text();
    }

    const segmentUrls = parseSegmentUrls(variantText, variantBaseUrl);
    const matches = await Promise.all(segmentUrls.map(url => cache.match(url)));
    const segmentsCached = matches.filter(Boolean).length;

    return {
      cached: segmentsCached === segmentUrls.length,
      segmentsCached,
      totalSegments: segmentUrls.length,
    };
  } catch {
    return empty;
  }
}

// --- Clear Video Cache ---

export async function clearVideoCache(
  masterManifestUrl?: string,
): Promise<void> {
  if (masterManifestUrl) masterManifestUrl = toAbsoluteUrl(masterManifestUrl);
  if (!masterManifestUrl) {
    await caches.delete(VIDEO_CACHE_NAME);
    return;
  }

  const cache = await caches.open(VIDEO_CACHE_NAME);
  const masterResponse = await cache.match(masterManifestUrl);
  if (!masterResponse) return;

  const masterText = await masterResponse.text();
  const baseUrl = baseUrlOf(masterManifestUrl);
  const variants = parseMasterManifest(masterText, baseUrl);

  await cache.delete(masterManifestUrl);

  if (variants.length === 0) {
    const urls = parseSegmentUrls(masterText, baseUrl);
    await Promise.all(urls.map(url => cache.delete(url)));
    return;
  }

  for (const variant of variants) {
    const res = await cache.match(variant.url);
    if (res) {
      const text = await res.text();
      const urls = parseSegmentUrls(text, baseUrlOf(variant.url));
      await Promise.all(urls.map(url => cache.delete(url)));
      await cache.delete(variant.url);
    }
  }
}

// --- Progress Listener ---

export function onCacheProgress(callback: ProgressCallback): () => void {
  progressListeners.push(callback);
  return () => {
    progressListeners = progressListeners.filter(cb => cb !== callback);
  };
}
